import { useTranslation } from "@/i18n";
import { AlertCircle, ChevronDown, ChevronUp } from "lucide-react";
import { useState } from "react";

/**
 * Props for the ErrorStep component.
 */
interface ErrorStepProps {
  /** Error message or details to display */
  errorDetails: string;
  /** Callback to restart the workflow */
  onRestart: () => void;
  /** Callback to return to document review */
  onBackToReview: () => void;
}

/**
 * Error step component shown when process import fails.
 * Displays error message and offers two actions:
 * 1. Upload a new document (restart workflow)
 * 2. Return to document review to try again
 *
 * Also includes a collapsible section to view full error details.
 *
 * @param props - Component props
 * @returns Error UI with action buttons
 */
export default function ErrorStep({
  errorDetails,
  onRestart,
  onBackToReview,
}: ErrorStepProps) {
  const { t } = useTranslation();
  const [showDetails, setShowDetails] = useState(false);

  return (
    <div className="flex flex-col items-center justify-center gap-4 p-8 h-full mx-auto my-48">
      <div className="flex items-center gap-2.5">
        <div className="w-8 h-8 rounded-full bg-red-500 flex items-center justify-center">
          <AlertCircle className="w-4 h-4 text-white" strokeWidth={1.33} />
        </div>
        <h2 className="text-lg font-semibold text-[#D6D6D6] leading-tight">
          {t("documentUpload.error.title")}
        </h2>
      </div>

      <p className="text-sm text-[#999999] max-w-md text-center">
        {t("documentUpload.error.message")}
      </p>

      {/* Error details collapsible section */}
      <div className="w-full max-w-md mt-2">
        <button
          onClick={() => setShowDetails(!showDetails)}
          className="flex items-center justify-between w-full px-4 py-2 bg-[#292929] border border-[#666666] rounded-lg text-white text-sm hover:bg-[#333333] transition-colors"
        >
          <span className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            {t("common.buttons.showErrorDetails")}
          </span>
          {showDetails ? (
            <ChevronUp className="w-4 h-4" />
          ) : (
            <ChevronDown className="w-4 h-4" />
          )}
        </button>

        {showDetails && (
          <div className="mt-2 p-4 bg-[#1a1a1a] border border-[#666666] rounded-lg">
            <pre className="text-xs text-[#999999] whitespace-pre-wrap break-words overflow-x-auto max-h-64 overflow-y-auto">
              {errorDetails}
            </pre>
          </div>
        )}
      </div>

      <div className="flex gap-3 mt-2">
        <button
          onClick={onBackToReview}
          className="flex items-center justify-center gap-2 px-3 py-1.5 bg-[#292929] border border-[#666666] rounded-lg text-white text-sm hover:bg-[#333333] transition-colors"
        >
          <span className="px-0.5 pb-0.5">
            {t("common.buttons.backToReview")}
          </span>
        </button>
        <button
          onClick={onRestart}
          className="flex items-center justify-center gap-2 px-3 py-1.5 bg-[#8E2BF0] border border-[#8E2BF0] rounded-lg text-white text-sm hover:bg-[#7A24D9] transition-colors"
        >
          <span className="px-0.5 pb-0.5">{t("common.buttons.newDocument")}</span>
        </button>
      </div>
    </div>
  );
}
